import { IClinic, IDoctor, Typestate } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { Input, Form, Row, Col, FormInstance, DatePicker, Select } from "antd";
import { useYup } from "@validations";
import { useTranslation } from "react-i18next";

import { PATH_CLINIC, enumJoinedProgram, region_towship } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import { clinicApi } from "@api";
import { useNotify } from "@utils";
import { useHistory } from "react-router";
import { AddIcon, SharedButton } from "@components";
import { DoctorTable } from "../doctor";
import dayjs from "dayjs";
import styled from "styled-components";
import { ModalForm } from "./Modal";

interface IProps {
  clinic?: IClinic;
}

const initValue: Partial<IClinic> = {
  clinicName: "",
  phoneNumber: "",
  email: "",
  clinicJoinDate: dayjs(),
  doctors: [],
  state: "",
  township: "",
  joinedProgram: [],
};

export const ClinicForm = memo(({ clinic }: IProps) => {
  const formRef = React.useRef<FormInstance>(null);
  const { YupClinic, YupUpdateClinic } = useYup();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { success, error } = useNotify();
  const clinicSchema = Yup.object().shape(clinic ? YupUpdateClinic : YupClinic);
  const [data, setData] = useState<Partial<IClinic>>(initValue);
  const [clinicForm] = Form.useForm();
  const [editDoctor, setEditDoctor] = useState<IDoctor>();
  const [formValid, setFormValid] = useState(true);
  const [add, addDoctor] = useState<boolean>(false);
  const [update, updateModal] = useState<boolean>(false);

  useEffect(() => {
    if (clinic) {
      setData({ ...data, ...clinic, clinicJoinDate: dayjs(clinic?.clinicJoinDate) });
      formRef.current?.setFieldsValue({
        ...data,
        ...clinic,
        state: clinic?.state.toLocaleLowerCase(),
        township: clinic?.township,
        clinicJoinDate: dayjs(clinic?.clinicJoinDate),
        confirmPassword: clinic?.password,
      });
    }
  }, [clinic]);

  const onFinish = async (values: IClinic) => {
    // if (!values.joinedProgram.includes(enumJoinedProgram.MNCH)) {
    //   error(t("page.you_must_be_select"));
    //   return;
    // }
    try {
      dispatch(setLoading(true));
      if (clinic) {
        if (notSameValue()) return;
        await clinicApi.update(
          {
            ...data,
            ...values,
            ...{ clinicJoinDate: dayjs(values.clinicJoinDate).format() },
          },
          data?.clinicId || ""
        );
      } else {
        await clinicApi.create({
          ...data,
          ...values,
          ...{ clinicJoinDate: dayjs(values.clinicJoinDate).format(), status: true },
        });
      }
      success(t("message.create.success"));
      history.push(PATH_CLINIC);
    } catch (err: any) {
      error(err?.response?.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const options = [
    {
      value: enumJoinedProgram.MNCH,
      label: enumJoinedProgram.MNCH,
    },
    {
      value: enumJoinedProgram.SRHR,
      label: enumJoinedProgram.SRHR,
    },
    {
      value: enumJoinedProgram.CDIC,
      label: enumJoinedProgram.CDIC,
    },
  ];
  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };
  const yupSync = {
    async validator({ field }: any, value: any) {
      await clinicSchema.validateSyncAt(field, { [field]: value });
    },
  };
  const doctors: IDoctor[] = Form.useWatch("doctors", clinicForm);
  const state: Typestate = Form.useWatch("state", clinicForm);
  const township: Typestate = Form.useWatch("township", clinicForm);
  const program: enumJoinedProgram[] = Form.useWatch("joinedProgram", clinicForm);
  const password: string = Form.useWatch("password", clinicForm);
  const confirmPassword: string = Form.useWatch("confirmPassword", clinicForm);

  const notSameValue = () => password !== confirmPassword;

  const handleDoctor = async (values: IDoctor) => {
    await clinicForm.setFieldValue("doctors", [...doctors, { ...values, key: doctors.length + 1 }]);
    await handleAddModal();
  };
  const handleDoctorUpdate = async (doctor: IDoctor) => {
    const updateDoctor = doctors;
    const index = updateDoctor.findIndex((item) => item?.key === doctor?.key);
    updateDoctor[index] = doctor;
    await clinicForm.setFieldValue("doctors", []);
    await clinicForm.setFieldValue("doctors", updateDoctor);
    await updateModal(false);
    await addDoctor(false);
    setEditDoctor(undefined);
  };
  const handleDoctorEdit = (doctor: IDoctor) => {
    setEditDoctor(doctor);
    handleUpdateModal();
  };
  const handleDoctorDelete = (doctor: IDoctor) => {
    const deleteFilter = doctors.filter((item) =>
      doctor?._id ? item._id !== doctor?._id : item.key !== doctor?.key
    );
    clinicForm.setFieldValue("doctors", deleteFilter);
  };
  const goBack = () => history.goBack();
  const isValid = () => !!clinicForm.getFieldsError().filter(({ errors }) => errors.length).length;
  const handleUpdateModal = () => updateModal(!update);
  const handleAddModal = () => {
    setEditDoctor(undefined);
    addDoctor(!add);
  };
  const handleChangeProgram = (value: enumJoinedProgram[]) => {
    // if (!value.includes(enumJoinedProgram.MNCH)) {
    //   error(t("page.you_must_be_select"));
    // } else {
      clinicForm.setFieldValue("joinedProgram", value);
    // }
  };

  return (
    <>
      <Form
        form={clinicForm}
        name="basic"
        ref={formRef}
        initialValues={data}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        onValuesChange={() =>
          setFormValid(clinicForm.getFieldsError().some((item) => item.errors.length > 0))
        }
      >
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.clinic_join_date")} name="clinicJoinDate" rules={[yupSync]}>
              <DatePicker />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.clinic_name")} name="clinicName" rules={[yupSync]}>
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.contact_phone_number")} name="phoneNumber" rules={[yupSync]}>
              <Input type="number" />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item label={t("page.register_email")} name="email" rules={[yupSync]}>
              <Input type="email" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item name={"state"} label={t("page.region_state")} rules={[yupSync]}>
              <Select>
                {Object.keys(region_towship).map((item) => (
                  <Select.Option value={item} key={item}>
                    {t(`page.${item}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item name={"township"} label={t("page.township")} rules={[yupSync]}>
              <Select value={township}>
                {state && region_towship[state] !== undefined &&
                  region_towship[state].map((item) => (
                    <Select.Option value={item} key={item}>
                      {item}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}>
            <Form.Item name={"joinedProgram"} label={t("page.joined_program")} rules={[yupSync]}>
              <Select
                mode="tags"
                style={{ width: "100%" }}
                placeholder="choose program"
                onChange={handleChangeProgram}
                value={program}
                options={options}
                showSearch={false}
              />
            </Form.Item>
          </Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            {clinic && (
              <Form.Item name={"password"} label={t("page.password")} rules={[yupSync]}>
                <Input type="text" />
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={{ md: 54, lg: 54 }}>
          <Col lg={12} md={24} sm={24} xs={24}></Col>
          <Col lg={12} md={24} sm={24} xs={24}>
            {clinic && (
              <>
                <Form.Item
                  name={"confirmPassword"}
                  label={t("page.confirm_password")}
                  rules={[yupSync]}
                >
                  <Input type="text" />
                </Form.Item>
                <StyledP>{notSameValue() ? "passwords must match" : ""}</StyledP>
              </>
            )}
          </Col>
        </Row>
        <Row>
          <Form.Item>
            <Row gutter={{ md: 54, lg: 54 }}>
              <Col lg={12} md={24} sm={24} xs={24}>
                <SharedButton type="link" icon={<AddIcon />} size="middle" onClick={handleAddModal}>
                  {t("page.add_doctor")}
                </SharedButton>
              </Col>
              <Col lg={12} md={24} sm={24}></Col>
            </Row>
          </Form.Item>
        </Row>
        {doctors && doctors.length > 0 && (
          <DoctorTable
            data={doctors}
            callbackEdit={handleDoctorEdit}
            callbackDelete={handleDoctorDelete}
          />
        )}
        {update && (
          <ModalForm
            doctor={editDoctor}
            open={update}
            onCancel={handleUpdateModal}
            callback={handleDoctor}
            update={handleDoctorUpdate}
          />
        )}
        {add && (
          <ModalForm
            open={add}
            onCancel={handleAddModal}
            callback={handleDoctor}
            update={handleDoctorUpdate}
          />
        )}
        <Form.Item name={"doctors"} rules={[yupSync]} />
        <Form.Item>
          <StyledFormAction>
            <SharedButton type="default" htmlType="button" size="middle" onClick={goBack}>
              {t("page.cancel")}
            </SharedButton>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.create")}
            </SharedButton>
          </StyledFormAction>
        </Form.Item>
      </Form>
    </>
  );
});

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
`;
const StyledP = styled.p`
  color: #ed0131;
`;
